/**
 * 初訪問の獲得経路を localStorage に控える。
 *
 * /api/learning-events/claim が受ける attribution と同じ形。
 * 「どこから来た人か」は訪問時にしか分からず、登録後に URL を
 * 見ても手遅れなので、最初に見せたページで書き留める。
 * 一度書いたら上書きしない (first touch)。claim 側は送られた接触を
 * first_touch には初回だけ、last_touch には都度記録する。
 */

/** api-go の learningevent.Touch と同じ上限。超えた分は切る */
const MAX_URL_FIELD_LENGTH = 2048;
const MAX_UTM_FIELD_LENGTH = 255;

const FIRST_TOUCH_KEY = "yumesaku:first-touch:v1";

export interface FirstTouch {
  /** 初訪問の時刻 (ISO 8601)。claim の seenAt に入る */
  seenAt: string;
  referrer?: string;
  landingPath?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
}

function clip(
  value: string | null | undefined,
  max: number,
): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.length > max ? trimmed.slice(0, max) : trimmed;
}

/**
 * 現在のページを「初訪問」として記録する。既に記録があれば何もしない。
 * どのページが着地でもいいよう、root の analytics 配線から毎ページ呼ぶ。
 */
export function recordFirstTouch(): void {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem(FIRST_TOUCH_KEY)) return;
    const params = new URLSearchParams(window.location.search);
    const touch: FirstTouch = {
      seenAt: new Date().toISOString(),
      referrer: clip(document.referrer, MAX_URL_FIELD_LENGTH),
      landingPath: clip(
        window.location.pathname + window.location.search,
        MAX_URL_FIELD_LENGTH,
      ),
      utmSource: clip(params.get("utm_source"), MAX_UTM_FIELD_LENGTH),
      utmMedium: clip(params.get("utm_medium"), MAX_UTM_FIELD_LENGTH),
      utmCampaign: clip(params.get("utm_campaign"), MAX_UTM_FIELD_LENGTH),
      utmTerm: clip(params.get("utm_term"), MAX_UTM_FIELD_LENGTH),
      utmContent: clip(params.get("utm_content"), MAX_UTM_FIELD_LENGTH),
    };
    window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(touch));
  } catch {
    // localStorage が使えない環境では経路の記録を諦める。画面は止めない
  }
}

/**
 * サイトの中の入口 (記事の中の診断の入口など) から来たときに、初訪問の記録の空いた utm を埋める。
 *
 * 初訪問は最初に開いた頁で 1 度だけ控えるので、検索から記事に来て記事の中の入口を押した人は、
 * 入口の utm が記録に残らなかった (初訪問は記事で、utm が無い)。そこで、記録の utm が 5 つとも空のときに限り、
 * 今の住所の utm で埋める。referrer と landingPath (どこから来て、どこに降りたか) は替えない。
 * 広告など外から utm 付きで来た人の記録は上書きしない。呼ぶのは入口の先の頁 (診断) だけで、どの頁でも埋めるわけではない
 */
export function fillCampaignIntoFirstTouch(search: string): void {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(search);
    const campaign = {
      utmSource: clip(params.get("utm_source"), MAX_UTM_FIELD_LENGTH),
      utmMedium: clip(params.get("utm_medium"), MAX_UTM_FIELD_LENGTH),
      utmCampaign: clip(params.get("utm_campaign"), MAX_UTM_FIELD_LENGTH),
      utmTerm: clip(params.get("utm_term"), MAX_UTM_FIELD_LENGTH),
      utmContent: clip(params.get("utm_content"), MAX_UTM_FIELD_LENGTH),
    };
    if (!campaign.utmSource) return;
    const stored = readFirstTouch();
    if (!stored) {
      recordFirstTouch();
      return;
    }
    if (stored.utmSource || stored.utmMedium || stored.utmCampaign || stored.utmTerm || stored.utmContent) return;
    window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify({ ...stored, ...campaign }));
  } catch {
    // 記録できなくても画面は止めない
  }
}

/**
 * 控えた初訪問を取り出す。読み出せない形・中身が全部空なら undefined。
 * 読んでも消さない。2 回目以降の名乗り (last_touch 更新) でまた使う。
 */
export function readFirstTouch(): FirstTouch | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem(FIRST_TOUCH_KEY);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return undefined;
    const touch = parsed as FirstTouch;
    if (typeof touch.seenAt !== "string" || !touch.seenAt) return undefined;
    return touch;
  } catch {
    return undefined;
  }
}
